import React from 'react';
import { Box, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  summaryCard: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'linear-gradient(180deg, rgba(20, 28, 55, 0.92), rgba(8, 12, 24, 0.92))',
    padding: '14px 14px 12px',
    marginBottom: 12,
  },
  summaryTitle: {
    color: '#e5e7ff',
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1.25,
    marginBottom: 8,
  },
  summaryText: {
    color: '#c9cff8',
    fontSize: 13,
    lineHeight: 1.6,
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 10,
  },
  chip: {
    borderRadius: 999,
    border: '1px solid rgba(77, 121, 255, 0.28)',
    background: 'rgba(77, 121, 255, 0.12)',
    color: '#cfd6ff',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
  },
  sectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  sectionCard: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.85)',
    padding: '12px 12px 10px',
    boxSizing: 'border-box',
    minHeight: 120,
  },
  sectionTitle: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  bullet: {
    color: '#e5e7ff',
    fontSize: 13,
    lineHeight: 1.5,
    marginBottom: 6,
    wordBreak: 'break-word',
  },
  empty: {
    color: '#8d93c9',
    fontSize: 12,
    lineHeight: 1.45,
  },
  footer: {
    color: '#9ea4d8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 12,
  },
  error: {
    color: '#ffb4b4',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 8,
  },
}));

function asArray(value) {
  return Array.isArray(value) ? value.filter(Boolean).map((item) => String(item)) : [];
}

export default function AIIntelligencePanel({ intelligence, summary, keySignals, riskSignals, confidence, loading, error }) {
  const classes = useStyles();
  const narrative = intelligence && intelligence.narrative ? intelligence.narrative : null;
  const whale = intelligence && intelligence.whale ? intelligence.whale : null;
  const resolvedSummary = summary || (narrative && narrative.summary) || 'No AI summary available.';
  const resolvedKeySignals = asArray(keySignals && keySignals.length ? keySignals : (narrative && narrative.keySignals));
  const resolvedRiskSignals = asArray(riskSignals && riskSignals.length ? riskSignals : (narrative && narrative.riskSignals));
  const resolvedConfidence = confidence || (narrative && narrative.confidence) || (loading ? 'Loading' : resolvedKeySignals.length ? 'Medium' : 'Low');

  return (
    <Box className={classes.panel}>
      <Typography className={classes.title}>AI Analysis</Typography>
      <Divider className={classes.divider} />

      <Box className={classes.summaryCard}>
        <Typography className={classes.summaryTitle}>E3D AI Summary</Typography>
        <Typography className={classes.summaryText}>{resolvedSummary || 'No AI summary available.'}</Typography>
        <Box className={classes.chipsRow}>
          <Box className={classes.chip}>Confidence {resolvedConfidence}</Box>
          <Box className={classes.chip}>{loading ? 'Refreshing analysis' : 'Analysis ready'}</Box>
          <Box className={classes.chip}>Whale {whale && Number.isFinite(Number(whale.score)) ? whale.score : 0}/100</Box>
        </Box>
      </Box>

      <Box className={classes.sectionGrid}>
        <Box className={classes.sectionCard}>
          <Typography className={classes.sectionTitle}>Key Signals</Typography>
          {resolvedKeySignals.length ? resolvedKeySignals.map((item) => (
            <Typography key={item} className={classes.bullet}>- {item}</Typography>
          )) : <Typography className={classes.empty}>No key signals available yet.</Typography>}
        </Box>

        <Box className={classes.sectionCard}>
          <Typography className={classes.sectionTitle}>Risk Signals</Typography>
          {resolvedRiskSignals.length ? resolvedRiskSignals.map((item) => (
            <Typography key={item} className={classes.bullet}>- {item}</Typography>
          )) : <Typography className={classes.empty}>No explicit risk signals available yet.</Typography>}
        </Box>
      </Box>

      <Typography className={classes.footer}>
        {loading ? 'Preparing AI interpretation from available market and narrative inputs…' : 'AI panel is currently heuristic-backed and ready for richer narrative models.'}
      </Typography>
      {error ? <Typography className={classes.error}>{error}</Typography> : null}
    </Box>
  );
}
